import { FeatureFlagsServiceBase } from '@skylab-shared/config';
import { HttpService, Query, QueryHandler, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { ModuleName } from '@skylab-shared/dataModel';
import { Transform, Type } from 'class-transformer';

export type ExpirationKind = 'Error' | 'Warning' | 'None';

export enum LicenseKind {
    Unknown = 'Unknown',
    Standardowa_1M = 'Standardowa_1M',
    Standardowa_3M = 'Standardowa_3M',
    Standardowa_12M = 'Standardowa_12M',
    UdostepnionaPrzezBiuroRachunkowe = 'UdostepnionaPrzezBiuroRachunkowe',
    Demonstracyjna_1M = 'Demonstracyjna_1M'
}

export type UserLicenseItem = UserLicenseItemDTO & {
    expirationKind: ExpirationKind;
};

type UserLicenses = {
    kind: LicenseKind;
    modules: UserLicenseItem[];
}

class UserLicenseItemDTO {
    public readonly active: boolean;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly expirationDate: Date;
    public readonly name: ModuleName;
}

class UserLicensesDTO {
    public readonly kind: LicenseKind;
    @Type(() => UserLicenseItemDTO) public modules: UserLicenseItemDTO[];
}

export class UserLicensesQuery extends Query<UserLicenses> { }

export class UserLicensesQueryHandlerBase implements QueryHandler<UserLicensesQuery, UserLicenses> {
    private static Url = TaggedUrl.join('license', [Tags.Api]);

    public constructor(
        private readonly $http: HttpService,
        private readonly featureFlags: FeatureFlagsServiceBase
    ) { }

    public async handleAsync(_: UserLicensesQuery): Promise<UserLicenses> {
        const { data } = await this.$http.get(UserLicensesQueryHandlerBase.Url, { dto: UserLicensesDTO });
        return {
            kind: data.kind,
            modules: data.modules.map(item => {
                return {
                    active: item.active,
                    expirationDate: new Date(item.expirationDate),
                    name: item.name,
                    expirationKind: this.getExpirationKind(item.expirationDate)
                };
            })
        };
    }

    private getExpirationKind(date: Date): ExpirationKind {
        if (!date) return 'Error';
        const { expiringLicenseErrorNotificationTerm: errorDays, expiringLicenseWarningNotificationTerm: warningDays } = this.featureFlags.flags;
        const expirationDate = new Date(date);
        const now = new Date();
        const diff = Math.floor((Date.UTC(expirationDate.getFullYear(), expirationDate.getMonth(), expirationDate.getDate()) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / (1000 * 60 * 60 * 24));

        if (diff <= errorDays) return 'Error';
        if (diff <= warningDays && diff > errorDays) return 'Warning';
        if (diff > warningDays) return 'None';

        return 'Error';
    }
}
